import React, { useEffect, useRef, useState } from 'react';
import { AlertContextProps, KeyedAlert, Alerts } from '../types';

export const AlertContext = React.createContext<AlertContextProps>({
  alerts: [],
  queueAlert: () => {
    return;
  },
  removeAlert: () => {
    return;
  },
});

export const isPersistent = (alert: KeyedAlert): boolean => {
  return Boolean(alert.persistent);
};

// Removes the oldest Alert
// Only considers entries that are not marked as "persistent"
export const removeOldestAlert = (
  setAlerts: (alerts: KeyedAlert[]) => void,
  alertsRef: React.MutableRefObject<KeyedAlert[]>
): void => {
  const alerts = [...alertsRef.current];
  for (let i = 0; i < alerts.length; i += 1) {
    const alert = alerts[i];
    if (!isPersistent(alert)) {
      alerts.splice(i, 1);
      setAlerts(alerts);
      break;
    }
  }
};

// Returns a function that allows removal of an alert by key
export const removeAlert = (
  setAlerts: (alerts: KeyedAlert[]) => void,
  alerts: KeyedAlert[]
): ((key: number) => void) => {
  return (key) => {
    const index = alerts.findIndex((alert) => alert.key === key);
    if (index >= 0) {
      const alertsCopy = [...alerts];
      alertsCopy.splice(index, 1);
      setAlerts(alertsCopy);
    }
  };
};

// Returns a function that allows adding an alert to the "queue"
export const queueAlert = (
  setAlerts: (alerts: KeyedAlert[]) => void,
  alerts: KeyedAlert[]
): ((alert: Alerts) => void) => {
  return (alert): void => {
    setAlerts([
      ...alerts,
      {
        ...alert,
        key: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
      },
    ]);
  };
};

export const AlertProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const [alerts, setAlerts] = useState<KeyedAlert[]>([]);
  const alertsRef = useRef(alerts);

  useEffect(() => {
    const timer = setTimeout(() => {
      removeOldestAlert(setAlerts, alertsRef);
    }, 6000);
    return (): void => clearTimeout(timer);
  }, [alerts]);
  return (
    <AlertContext.Provider
      value={{
        alerts: alerts,
        queueAlert: queueAlert(setAlerts, alerts),
        removeAlert: removeAlert(setAlerts, alerts),
      }}
    >
      {children}
    </AlertContext.Provider>
  );
};
